/**
 * The cob chain: maize cob dried and shelled at a THIRD-PARTY facility, then sent to our plants.
 *
 * Cob arrives by truck at a dryer that is not one of our plants (a `facilities` row with
 * `type = 'external'`, holding its own warehouse tree). There it is stored, optionally blended,
 * dried and shelled — cob in, seed out — put away again, bagged, and dispatched truck by truck.
 *
 * WHY SIX FORMS AND NOT ONE. The operator sees one screen with five tabs, but each stage is its
 * own `documents` row, chained through `source_document_id`. A single row cannot carry the chain:
 * `document_lines` has exactly one `lot_id`/`output_lot_id` pair while every stage consumes one lot
 * generation and produces the next; `reverseDocument` reverses a whole document, so a mid-chain
 * cancel would unwind the stages before it; and dispatch is N documents anyway, one per truck.
 * `setup_stage`/`unload_stage`/`sloc_stage` are not a counter-example — those track physical
 * progress inside ONE document's own work.
 *
 * The three middle legs are `doc_type = 'processing'`, the name `docs/PLAN.md` §5.3 already
 * reserves for issue/process/receive/relocate work, rather than four new document types.
 */
import { fullTransport } from "./arrival";
import type { FormConfig } from "./types";

const base = {
  numberPad: 4,
  // The cob chain weighs by lot, not by sack. If a dryer ever starts weighing cobs bag by bag this
  // flips per form, and the handheld picks the form up with no other change.
  bagWise: false,
  qaRequired: false,
  multipleMaterials: true,
  blindCoded: false,
  enforceUniqueLotNumber: true,
  dcInfo: false,
};

/**
 * A lot's SP pair as the desk reads it: `AO611 X AO612`, female first (user, 2026-09-29). This is
 * Null unless both codes are there.
 */
export function spPairOf(
  spFemale: string | null | undefined,
  spMale: string | null | undefined
): string | null {
  const f = spFemale?.trim();
  const m = spMale?.trim();
  return f && m ? `${f} X ${m}` : null;
}

/**
 * What the Variety cell shows on the cob chain: the variety, else "<Crop> - Coded" for a Maize lot
 * whose SP pair is not decoded yet (user, 2026-10-15; was the SP codes since 2026-10-13 — the pair
 * has its own SP Code column). A dryer blend lot with no shared SP pair stays blank.
 */
export function cobVarietyText(
  variety: string | null | undefined,
  cropCode: string | null | undefined,
  spFemale: unknown,
  spMale: unknown,
  cropName?: string | null
): string | null {
  if (variety) return variety;
  if (cropCode !== "MZ") return null;
  const f = typeof spFemale === "string" ? spFemale.trim() : "";
  const m = typeof spMale === "string" ? spMale.trim() : "";
  if (!f && !m) return null;
  return `${cropName?.trim() || "Maize"} - Coded`;
}

/** Every stage after the first happens bin-to-bin inside the dryer's own warehouse. */
const inPlace = {
  ...base,
  docType: "processing" as const,
  sourceKind: "storage" as const,
  destKind: "storage" as const,
  chainRole: "stage" as const,
  // A third-party dryer does not always track bins: every stage may leave its lots unbinned.
  slocOptional: true,
  lotSource: "manual" as const,
  allowManualLots: false,
  declaredFeed: false,
  transport: { ...fullTransport, enabled: false },
};

/**
 * Stage 1 — cob arriving at the third-party dryer, entered off the truck's paper SEED DISPATCH NOTE.
 *
 * The form carries what that note carries and nothing more: DC No., Lorry No., Driver,
 * From / To Address and Dispatch Date above; one row per lot — the note's PDN No. (shown as Lot No.),
 * Quantity, No. of Bag and Remarks (shown as Moisture %) — and Remarks and Production Personnel Name. The cob chain carries no Hybrid
 * Code (user 2026-10-14). The truck's weighbridge readings are the one addition: weighed loaded, weighed again empty,
 * and the difference is the actual total quantity the note recovered. Crop and variety are not on
 * the note, so these lots carry none.
 *
 * MANUAL ENTRY. The lot number typed off the note IS the lot's identifier (`typedLotIdentifier`): stored
 * as typed, unique system-wide, never minted from a plant series. Only a blend renumbers — its merged
 * lot takes the next M8-series number; lots left out of the blend keep the number typed here.
 *
 * SLOC IS OPTIONAL. A third-party dryer does not always tell us its bins, so a lot may be submitted
 * without one; its stock then sits unbinned at the facility until a blend or a drying run bins it.
 *
 * QA IS OFF, and that is a deliberate exception to A10 ("every arrival's lots need QA"). There is
 * no lab at a third-party dryer, and a lot minted `under_testing` there could never be dispatched
 * once the QA gate lands, because only eligible stock may move. QA opens at the plant that
 * receives it — see `arrivalCobReceipt`.
 */
export const arrivalCob: FormConfig = {
  ...base,
  code: "arrival_cob",
  docType: "arrival",
  title: "COB Arrival",
  numberPrefix: "AC",
  numberPad: 1,
  sourceKind: "vendor",
  destKind: "storage",
  declaredLabel: null,
  declaredFeed: false,
  lotSource: "manual",
  // S-Capture now sends the note itself (the COB feed): the dryer picks the truck instead of typing.
  feedPrefill: "scapture_cob",
  typedLotIdentifier: true,
  allowManualLots: true,
  receivedCaptured: true,
  // No transport block: the note's Lorry No. and Driver are header fields, and a courier bill number
  // is not a vehicle number, so nothing checks its format.
  transport: { ...fullTransport, enabled: false, vehicleValidation: "off" },
  // SLOC is mandatory on the arrival (user 2026-10-14): every lot is binned before Submit. The dryer's
  // own steps (blend, drying, load) may still hold stock unbinned, and so may arrivals submitted
  // before this.
  slocOptional: false,
  // Both truck readings are typed; their difference against the note's own total sets every lot's
  // actual quantity.
  recoveryFromWeighbridge: true,
  chainRole: "head",
  chainStage: "arrival",
  // Crop and Stage first; then the note's own order. `packRows` lays them two per row.
  headerFields: [
    "header_crop",
    "header_stage",
    "note_serial_no",
    "lorry_no",
    "driver_name",
    "driver_mobile",
    "from_address",
    "to_address",
    "dispatch_date",
    "production_person_name",
    "loaded_weight",
    "empty_weight",
    "net_weight",
    "recovery_pct",
  ],
  // Both readings are required: net drives every lot's actual quantity, so one reading alone would
  // submit a note whose lots recovered nothing.
  requiredHeaderFields: [
    "header_crop",
    "header_stage",
    "dispatch_date",
    "loaded_weight",
    "empty_weight",
  ],
  // Cob arrives as maize cob, on the day it is raised: all three are shown, none is typed.
  lockedHeaderFields: ["arrival_date", "header_crop", "header_stage"],
  // Every lot on the note inherits these; the operator can change them.
  headerDefaults: { crop: "MZ", stage: "CB" },
  // The note's lots are typed across a table and posted together, not looked up one by one.
  lotEntry: "rows",
  // The note's "Date Starting" is S-Capture's dispatch date, and is named so (user, 2026-10-15). On
  // a truck booked from the COB feed it is the feed's, and locked (`enforceFeedDispatchDate`).
  headerLabels: { dispatch_date: "Dispatch Date" },
  // Quantity is the SDN's figure (declared); Actual Qty is what the truck weights recover of it.
  // The note's own bag count sits beside it, so the bags counted off the truck read against it
  // (user, 2026-09-29: show what S-Capture sent while working the lots). Each lot's SP pair reads
  // `F X M`.
  lineColumns: [
    "lot_number",
    "sp_pair",
    "declared_bags",
    "declared_qty",
    "received_bags",
    "moist_pct",
    "received_qty",
    "storage",
    "actions",
  ],
  lineGroups: [
    { label: "Dispatch Note", columns: ["declared_bags", "declared_qty"] },
  ],
  columnLabels: {
    lot_number: "Lot No.",
    declared_bags: "Bags",
    declared_qty: "Quantity",
    received_bags: "No. of Bags",
    moist_pct: "Moisture %",
    received_qty: "Actual Qty",
  },
  detailSections: [
    {
      blocks: [
        {
          kind: "fields",
          fields: [
            { key: "lot_number", label: "Lot No.", readOnlyWhen: "always" },
            { key: "declared_qty", label: "Quantity", required: true },
            { key: "received_bags", label: "No. of Bags", required: true },
            { key: "moist_pct", label: "Moisture %" },
            {
              key: "received_qty",
              label: "Actual Qty",
              readOnlyWhen: "always",
            },
          ],
        },
      ],
    },
    { title: "Storage Location (SLOC) — optional", blocks: [{ kind: "sloc" }] },
  ],
  documents: ["seed_dispatch_note", "cob_arrival_receipt_note"],
};

/**
 * Stage 2 — blending, OPTIONAL and REPEATABLE.
 *
 * N lots from THIS ARRIVAL become one blend lot. Skipping is not a flag: a chain with no blend
 * document simply never blended, so there is nothing to clean up when the operator changes their
 * mind.
 *
 * One truck may be blended several times — three lots into one lot, then two more into another —
 * because what can be blended together is a question about the seed, not about the truck. So this
 * form is `repeatable` (like Dispatch), the lots already consumed drop out of the picker, and the
 * truck stays in the Blend queue until the operator starts the drying run.
 *
 * The blend page asks for no storage: the blended lot is held unbinned at the dryer, the same
 * `facility` balance an unbinned arrival lot sits in.
 */
export const cobBlend: FormConfig = {
  ...inPlace,
  code: "cob_blend",
  title: "Blend Lots",
  numberPrefix: "BLD",
  declaredLabel: null,
  receivedCaptured: true,
  chainStage: "blend",
  headerFields: [],
  /*
   * What went in, and what it was. There is no "Into Blend" pair to type: a member goes in whole,
   * and `submitBlend` reads each one's own balance — a typed figure was a box that changed nothing.
   */
  lineColumns: [
    "source_lot",
    "crop",
    "variety",
    "stage",
    "source_sloc",
    "available_bags",
    "available_qty",
    "actions",
  ],
  lineGroups: [
    { label: "Available", columns: ["available_bags", "available_qty"] },
  ],
  repeatable: true,
  /*
   * Nothing. The number is minted at submit, its crop, variety and stage are the members' own — all
   * in the grid above — and the output is held unbinned at the dryer.
   */
  detailSections: [],
  documents: [],
};

/**
 * Stage 3 — drying and shelling.
 *
 * IN PLACE. seedtrac's Drying Slip shows the same bin on both sides of the run
 * (`WH-01/B17/16[2] 29.100 → WH-01/B17/16[2] 28.500`, loss 0.600 / 2.06 %, `Machine 01`): the
 * dryer is a detail column, not a location. There is no activity master in this schema and no
 * activity-to-activity transaction is needed — the seed never leaves the bin.
 *
 * Shelling happens here — cob goes in and maize seed comes out — but NO lot is renumbered: every lot
 * keeps its number through the run, the blend's M8 lot included. After a blend, the lots left out of
 * it go through the same run as the M8 lot.
 *
 * The dryer reports ONE WEIGHT PER LOT, so the post-drying figure is a per-line column and this
 * form never distributes a single number across several lines. That is rule A3 holding: if a dryer
 * ever hands back only a batch total, the operator blends first so the batch IS one lot.
 */
export const cobDrying: FormConfig = {
  ...inPlace,
  code: "cob_drying",
  title: "Drying & Shelling",
  numberPrefix: "DRY",
  declaredLabel: "Before Drying",
  receivedCaptured: true,
  chainStage: "drying",
  headerFields: [],
  lineColumns: [
    "lot_number",
    "crop",
    "variety",
    "source_sloc",
    "declared_bags",
    "declared_qty",
    "post_drying_bags",
    "post_drying_qty",
    "drying_loss",
    "drying_loss_pct",
    "drying_start",
    "drying_end",
    "drying_hours",
    "drying_details",
    "actions",
  ],
  lineGroups: [
    { label: "Before Drying", columns: ["declared_bags", "declared_qty"] },
    {
      label: "After Drying-Shelling",
      columns: ["post_drying_bags", "post_drying_qty"],
    },
    { label: "Drying Loss", columns: ["drying_loss", "drying_loss_pct"] },
  ],
  detailSections: [
    {
      blocks: [
        {
          kind: "fields",
          fields: [
            { key: "lot_number", readOnlyWhen: "always" },
            { key: "source_sloc", readOnlyWhen: "always" },
          ],
        },
        {
          kind: "matrix",
          columns: ["Before Drying", "After Drying-Shelling", "Drying Loss"],
          rows: [
            {
              label: "No. of Bags (NoB)",
              cells: ["declared_bags", "post_drying_bags", null],
            },
            {
              label: "Quantity",
              cells: ["declared_qty", "post_drying_qty", "drying_loss"],
              // The loss PERCENTAGE shares the loss cell, the way Tare Weight shares Actual.
              extra: { key: "drying_loss_pct", column: 2 },
            },
          ],
        },
      ],
    },
    {
      title: "Drying Run",
      blocks: [
        {
          kind: "fields",
          fields: [
            { key: "drying_start", required: true },
            { key: "drying_end", required: true },
            { key: "drying_hours", readOnlyWhen: "always" },
            { key: "drying_details" },
          ],
        },
      ],
    },
    // Where the dried lot goes. Left empty, a lot in one bin goes back to that bin; a lot that
    // arrived with no SLOC has to be given one here.
    { title: "Updated SLOC — optional", blocks: [{ kind: "sloc" }] },
  ],
  documents: ["drying_slip"],
};

/**
 * Stage 5 — dispatch to one of our plants. N of these per chain, one per truck.
 *
 * Each truck draws the dried lot down and posts it to `in_transit`; the receiving plant draws it
 * out again on its own document. No child lot is minted per truck: a blended lot is stored bare
 * and has no batch segment to bump, and having blended and un-blended lots number differently
 * would be worse than having neither do it. Per-truck provenance is the document itself.
 * A lot split over two trucks is received at the plant once, whole, when both are in (2026-11-04).
 *
 * Seed is bagged before it goes, and the truck's NoB is what the paperwork carries. The bags are
 * COUNTED, not weighed one by one: per-bag weighing is a handheld capability at our own plants,
 * and this stage happens at a third party where our handhelds are not. The receiving plant weighs
 * bag by bag if it wants to — that is `arrival_cob_receipt`, on our own floor.
 */
export const cobDispatch: FormConfig = {
  ...base,
  code: "cob_dispatch",
  docType: "dispatch",
  title: "Load Truck to Plant",
  numberPrefix: "CDS",
  headerLabels: { arrival_date: "Load Date" },
  sourceKind: "storage",
  destKind: "storage",
  declaredLabel: "Available",
  declaredFeed: false,
  lotSource: "manual",
  allowManualLots: false,
  receivedCaptured: true,
  transport: fullTransport,
  dcInfo: true,
  chainRole: "stage",
  chainStage: "dispatch",
  slocOptional: true,
  repeatable: true,
  headerFields: [
    "dispatch_date",
    "dest_plant",
    "header_crop",
    "dc_date",
    "dc_number",
    // The dryer weighs the truck empty, loads it, then weighs it loaded (user, 2026-10-15).
    "empty_weight",
    "loaded_weight",
    "net_weight",
  ],
  requiredHeaderFields: ["dispatch_date", "dest_plant"],
  // Copied off the truck's Cob Arrival when the truck is added: the seed is already what it is.
  lockedHeaderFields: ["header_crop"],
  // No variety: a cob lot never has one.
  lineColumns: [
    "lot_number",
    "crop",
    "source_sloc",
    "available_bags",
    "available_qty",
    "dispatch_bags",
    "dispatch_qty",
    "actions",
  ],
  lineGroups: [
    { label: "Available", columns: ["available_bags", "available_qty"] },
    { label: "This Truck", columns: ["dispatch_bags", "dispatch_qty"] },
  ],
  /*
   * Declared rather than derived: the derived panel turns every editable grid column into a box, so
   * it asked for Crop and Variety again on seed whose crop was settled at arrival. What is left to
   * type about a lot on a truck is how much of it goes.
   */
  detailSections: [
    {
      blocks: [
        {
          kind: "fields",
          fields: [
            { key: "lot_number", readOnlyWhen: "always" },
            { key: "source_sloc", readOnlyWhen: "always" },
          ],
        },
        {
          kind: "matrix",
          columns: ["Available", "This Truck"],
          rows: [
            {
              label: "No. of Bags (NoB)",
              cells: ["available_bags", "dispatch_bags"],
            },
            {
              label: "Quantity",
              cells: ["available_qty", "dispatch_qty"],
            },
          ],
        },
      ],
    },
  ],
  documents: ["cob_dispatch_note"],
};

/**
 * Stage 6 — Maize-Dried Seed Arrival: the receiving plant's own document, raised at the plant by
 * the plant's operator when the truck from the dryer lands.
 *
 * A separate document rather than a second phase of the dispatch, because the receiver is a
 * different user at a different facility: facility scoping can only enforce that boundary across
 * two documents. It is an ordinary arrival, so it gets an ordinary nav entry — the plant does not
 * reach it through the dryer's screen.
 *
 * THIS is where QA opens, and where a bare third-party lot number is finally stamped with a plant
 * code (`M80001` starts displaying as `DM80001`; the stored string never changes).
 */
export const arrivalCobReceipt: FormConfig = {
  ...base,
  code: "arrival_cob_receipt",
  docType: "arrival",
  receivesFrom: "cob_dispatch",
  title: "Maize-Dried Seed Arrival",
  numberPrefix: "AM",
  numberPad: 1,
  sourceKind: "vendor",
  destKind: "storage",
  declaredLabel: "Dispatch",
  declaredFeed: true,
  // The dried seed reaches the plant on an S-Capture dispatch, so its lots come off the same feed
  // and the same inbox picker as `arrival_scapture` rather than being typed in.
  lotSource: "scapture",
  allowManualLots: true,
  // Bag-wise on the handheld. Set here, not on the cob `base`, which the four processing stages
  // and the dryer-side Cob Arrival also spread.
  bagWise: true,
  receivedCaptured: true,
  qaRequired: true,
  transport: fullTransport,
  headerFields: ["source_plant", "address"],
  // Seed back from a dryer, not a transfer between our plants (2026-09-29).
  headerLabels: { source_plant: "From Dryer" },
  lineColumns: [
    "lot_number",
    "crop",
    "variety",
    "declared_bags",
    "declared_qty",
    "received_bags",
    "received_qty",
    "difference_bags",
    "difference",
    "stage",
    "moist_pct",
    "pp",
    "qc_status",
    "storage",
    "actions",
  ],
  lineGroups: [
    { label: "Dispatch", columns: ["declared_bags", "declared_qty"] },
    { label: "Received", columns: ["received_bags", "received_qty"] },
    { label: "Difference", columns: ["difference_bags", "difference"] },
  ],
  // Bag-wise, so it prints the Raw Seed Unloading Sheet (and spends an RSUS No. at Preview) like
  // S-Capture and Stock Transfer In (user, 2026-09-30).
  documents: ["dryer_receipt_note", "unloading_sheet"],
  provisional: true,
};
